/**
 * FIDDUX PDF Report - Templates institucionais por audiência.
 *
 * Define a estrutura canônica (nível institucional, referência Morgan Stanley /
 * BlackRock) para os dois públicos do report:
 *   - internal: trading, macro global, estratégias, hedging (uso interno).
 *   - client: motivar clientes a investir mais na Fiddux e mostrar por que tirar
 *     capital do Brasil (tom calmo, institucional).
 *
 * Também guarda a persona de áudio (voz de analista sênior) usada tanto para o
 * Google Cloud TTS quanto para o script copiado para o NotebookLM, garantindo
 * consistência entre os reports.
 */

export const AUDIENCES = {
  internal: 'internal',
  client: 'client',
};

export const AUDIENCE_LABELS = {
  internal: 'Interno',
  client: 'Cliente',
};

/**
 * Regra permanente: áudio e narrativa como material proprietário Fiddux.
 * Nunca citar sources, links, vídeos, autores ou terceiros.
 */
export const AUDIO_NO_SOURCES_RULE_EN =
  "CRITICAL — Proprietary internal material: NEVER mention or allude to external sources, " +
  "links, URLs, YouTube, podcasts, videos, articles, research providers, bank names used as " +
  "sources, channel names, presenters, analysts, or any person's name from source material. " +
  "Do NOT say 'according to', 'the video shows', 'as reported by', 'in this podcast', or cite " +
  "third parties. Synthesize insights only as original FIDDUX Global Research desk analysis.";

export const AUDIO_NO_SOURCES_RULE_PT =
  "CRÍTICO — Material interno proprietário: NUNCA mencione ou aluda a sources externas, " +
  "links, URLs, YouTube, podcasts, vídeos, artigos, provedores de pesquisa, nomes de canais, " +
  "apresentadores, analistas ou qualquer nome de pessoa vinda do material de origem. NÃO diga " +
  "'segundo', 'no vídeo', 'como reportado por', 'neste podcast' ou cite terceiros. Sintetize " +
  "apenas como análise original do desk FIDDUX Global Research.";

/**
 * Persona de áudio — usada no TTS e no script do NotebookLM.
 * Mantém o mesmo tom em todos os reports (resolve "às vezes fica horrível").
 */
export const AUDIO_PERSONA_EN =
  "Male voice. Calm, professional, and quietly disciplined. Sounds like a senior " +
  "analyst delivering an institutional market update. Tone should be reassuring, " +
  "clear, and precise, without panic, sales energy, or emotional pressure. Focus on " +
  "the subject of the document given, and the strategic relevance for our company and " +
  "trading/allocation strategy as well as to our clients. Keep regulation brief, " +
  "neutral, and understated. Do not use fear-based language, crisis framing, or " +
  "exaggerated warnings. Do not mention money laundering, crackdown, 'rigorous funnel', " +
  "or 'not free of risk'. Pronounce the company name as 'Fiddux' correctly and " +
  "consistently. " +
  AUDIO_NO_SOURCES_RULE_EN;

export const AUDIO_PERSONA_PT =
  "Voz masculina. Calma, profissional e discretamente disciplinada. Soa como um " +
  "analista sênior entregando uma atualização institucional de mercado. O tom deve ser " +
  "tranquilizador, claro e preciso, sem pânico, sem energia de venda e sem pressão " +
  "emocional. Foco no assunto do documento e na relevância estratégica para a nossa " +
  "empresa e para a estratégia de trading/alocação, bem como para os nossos clientes. " +
  "Mantenha a parte regulatória breve, neutra e discreta. Não use linguagem baseada em " +
  "medo, enquadramento de crise ou avisos exagerados. Pronuncie o nome da empresa como " +
  "'Fíddux' de forma correta e consistente. " +
  AUDIO_NO_SOURCES_RULE_PT;

/** Texto extra ao copiar para o NotebookLM (instruções para o podcast gerado lá). */
export const NOTEBOOKLM_COPY_PREAMBLE_EN =
  AUDIO_PERSONA_EN +
  "\n\nNotebookLM instructions: The audio overview must read as proprietary FIDDUX internal " +
  "research. Do not name sources, hosts, channels, URLs, or external institutions. " +
  "Do not discuss where the information came from.";

export const NOTEBOOKLM_COPY_PREAMBLE_PT =
  AUDIO_PERSONA_PT +
  "\n\nInstruções NotebookLM: O áudio deve soar como pesquisa interna proprietária da FIDDUX. " +
  "Não nomeie sources, apresentadores, canais, URLs ou instituições externas. " +
  "Não discuta de onde veio a informação.";

/**
 * Pronúncia alvo para SSML do Google TTS (<sub alias="...">Fiddux</sub>).
 */
export const FIDDUX_TTS_ALIAS = 'Fidducks';

/** Estilos de áudio TTS integrado (Google Gemini TTS + fallback Chirp 3 HD). */
export const AUDIO_TTS_STYLES = [
  {
    value: 'classic',
    label: 'Clássico (recomendado)',
    hint: 'Voz masculina institucional Chirp 3 HD — estável e confiável.',
  },
  {
    value: 'expressive',
    label: 'Narrador expressivo',
    hint: 'Uma voz masculina Gemini TTS com tom mais dinâmico.',
  },
  {
    value: 'deep_dive',
    label: 'Deep Dive (experimental)',
    hint: 'Duas vozes masculinas em diálogo — pode variar; use NotebookLM se preferir.',
  },
];

export const DEFAULT_AUDIO_TTS_STYLE = 'classic';

/**
 * Defaults de capa/rodapé/confidencialidade por audiência.
 * São aplicados no estado `cover` do hub quando a audiência muda.
 */
export const COVER_PRESETS = {
  internal: {
    cover_subtitle: 'Internal Strategy & Trading Memorandum',
    cover_subtitle_pt: 'Memorando Interno de Estratégia e Trading',
    prepared_by: 'FIDDUX Global Research',
    prepared_by_pt: 'FIDDUX Global Research',
    cover_date: '{date} | Strictly Confidential | Internal Use Only',
    cover_date_pt: '{date} | Estritamente Confidencial | Uso Interno',
    footer_branding: 'FIDDUX | Internal Strategy & Trading',
    footer_branding_pt: 'FIDDUX | Estratégia e Trading Internos',
    footer_minimal: true,
  },
  client: {
    cover_subtitle: 'Institutional Research — Exclusive for FIDDUX Clients',
    cover_subtitle_pt: 'Pesquisa Institucional — Exclusivo para Clientes FIDDUX',
    prepared_by: 'FIDDUX Global Research Institute',
    prepared_by_pt: 'FIDDUX Global Research Institute',
    cover_date: '{date} | Private & Confidential | For Clients',
    cover_date_pt: '{date} | Privado e Confidencial | Para Clientes',
    footer_branding: 'FIDDUX Global Research',
    footer_branding_pt: 'FIDDUX Global Research',
    footer_minimal: true,
  },
};

/**
 * Estrutura canônica de seções por audiência (títulos EN/PT).
 * Usada para gerar um template vazio e como contrato para a IA.
 */
export const SECTION_BLUEPRINTS = {
  internal: [
    { title: 'Executive Summary', title_pt: 'Sumário Executivo' },
    { title: 'Market Regime', title_pt: 'Regime de Mercado' },
    { title: 'Trading Framework', title_pt: 'Estrutura de Trading' },
    { title: 'Strategy & Hedging', title_pt: 'Estratégia e Hedge' },
    { title: 'What to Monitor', title_pt: 'O Que Monitorar' },
    { title: 'Risk Matrix', title_pt: 'Matriz de Riscos' },
    { title: 'Action Items', title_pt: 'Ações' },
    { title: 'Important Disclosures', title_pt: 'Divulgações Importantes' },
  ],
  client: [
    { title: 'Executive Summary', title_pt: 'Sumário Executivo' },
    { title: 'Market Context — Why It Matters', title_pt: 'Contexto de Mercado — Por Que Importa' },
    { title: 'Brazil & Capital Allocation', title_pt: 'Brasil e Alocação de Capital' },
    { title: 'Fiddux Strategy & Positioning', title_pt: 'Estratégia e Posicionamento Fiddux' },
    { title: 'Opportunity for Clients', title_pt: 'Oportunidade para Clientes' },
    { title: 'Risk Framework', title_pt: 'Estrutura de Risco' },
    { title: 'Outlook', title_pt: 'Perspectiva' },
    { title: 'Important Disclosures', title_pt: 'Divulgações Importantes' },
  ],
};

const DOCUMENT_TYPE_BY_AUDIENCE = {
  internal: 'internal_memo',
  client: 'client_research',
};

/**
 * Monta um schema JSON vazio (template inicial) para a audiência escolhida.
 */
export function buildEmptyTemplate(audience = AUDIENCES.client) {
  const aud = COVER_PRESETS[audience] ? audience : AUDIENCES.client;
  const preset = COVER_PRESETS[aud];
  const blueprint = SECTION_BLUEPRINTS[aud];
  return {
    report_meta: {
      title: '',
      title_pt: '',
      date: new Date().toISOString().split('T')[0],
      audience: aud,
      document_type: DOCUMENT_TYPE_BY_AUDIENCE[aud],
      cover_subtitle: preset.cover_subtitle,
      cover_subtitle_pt: preset.cover_subtitle_pt,
      prepared_by: preset.prepared_by,
      prepared_by_pt: preset.prepared_by_pt,
      cover_date: preset.cover_date,
      cover_date_pt: preset.cover_date_pt,
      footer_branding: preset.footer_branding,
      footer_branding_pt: preset.footer_branding_pt,
      footer_minimal: preset.footer_minimal,
    },
    sections: blueprint.map((s, i) => ({
      id: `s${i + 1}`,
      order: i + 1,
      type: 'section',
      title: s.title,
      title_pt: s.title_pt,
      level: 1,
      include_page_break: i > 0,
      content: '',
      content_pt: '',
      tables: [],
      subsections: [],
    })),
    audio_script: '',
    audio_script_pt: '',
  };
}

/**
 * slug curto e legível a partir de um título (para nome de arquivo).
 */
export function slugifyTitle(title, maxWords = 5) {
  if (!title || typeof title !== 'string') return 'Report';
  const cleaned = title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s-]/g, ' ')
    .trim();
  const words = cleaned.split(/\s+/).filter(Boolean).slice(0, maxWords);
  const slug = words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('-');
  return slug || 'Report';
}

/**
 * Nome de arquivo descritivo:
 *   FIDDUX_{Cliente|Interno}_{Slug-do-Titulo}_{YYYY-MM-DD}_{PT|EN}.pdf
 */
export function buildReportFilename({ title, audience, date, lang, ext = 'pdf' }) {
  const audLabel = audience === AUDIENCES.internal ? 'Interno' : 'Cliente';
  const slug = slugifyTitle(title);
  const dateStr = date || new Date().toISOString().split('T')[0];
  const langStr = (lang || 'en').toUpperCase();
  return `FIDDUX_${audLabel}_${slug}_${dateStr}_${langStr}.${ext}`;
}
